import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Completeness, ConsentBadge, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function NewPatientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", dob: "", sex: "female", abha_id: "", phone: "", email: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/patients", f); toast.success("Patient record created"); setOpen(false); onCreated(data); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-patient-button" className="bg-teal-700 hover:bg-teal-800 rounded-full"><Plus className="w-4 h-4 mr-1" /> New patient</Button></DialogTrigger>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="new-patient-dialog">
        <DialogHeader><DialogTitle className="font-heading">Create patient record</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Full name *</Label><Input data-testid="patient-name-input" required value={f.name} onChange={set("name")} /></div>
          <div><Label>Date of birth *</Label><Input data-testid="patient-dob-input" type="date" required value={f.dob} onChange={set("dob")} /></div>
          <div><Label>Sex *</Label>
            <Select value={f.sex} onValueChange={(v) => setF({ ...f, sex: v })}><SelectTrigger data-testid="patient-sex-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
          <div><Label>ABHA ID</Label><Input data-testid="patient-abha-input" value={f.abha_id} onChange={set("abha_id")} placeholder="91-xxxx-xxxx-xxxx" /></div>
          <div><Label>Phone</Label><Input data-testid="patient-phone-input" value={f.phone} onChange={set("phone")} /></div>
          <div className="col-span-2"><Label>Patient email (links patient portal login)</Label><Input data-testid="patient-email-input" type="email" value={f.email} onChange={set("email")} /></div>
          <div className="col-span-2 flex justify-end pt-2"><Button data-testid="patient-create-submit" className="bg-teal-700 hover:bg-teal-800 rounded-full">Create record</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PatientsPage() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/patients").then((r) => setPatients(r.data));
  useEffect(() => { load(); }, []);
  const canCreate = ["admin", "doctor", "staff"].includes(user.role);
  const list = patients.filter((p) => p.name?.toLowerCase().includes(q.toLowerCase()) || p.abha_id?.includes(q));

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Records</p><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Patients</h1></div>
        <div className="flex gap-3 items-center">
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input data-testid="patient-search-input" className="pl-9 w-56" placeholder="Search name or ABHA" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          {canCreate && <NewPatientDialog onCreated={load} />}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5" data-testid="patients-list">
        {list.map((p) => (
          <Link to={`/patients/${p.id}`} key={p.id} data-testid={`patient-row-${p.id}`} className="clinical-card p-6 sm:p-7 flex flex-col gap-5 hover:shadow-md">
            <div className="flex items-start gap-4">
              <span className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-800 grid place-items-center font-heading font-bold text-xl shrink-0">{p.name?.[0]}</span>
              <div className="flex-1 min-w-0"><p className="font-heading font-bold text-lg sm:text-xl text-slate-900">{p.name}</p><p className="text-sm text-slate-500 mt-0.5">{p.sex} · DOB {fmtDate(p.dob)}</p><p className="text-xs text-slate-400 font-mono mt-0.5">{p.abha_id || "no ABHA"}</p></div>
              <StatusBadge status={p.latest_case_status} testId={`patient-status-${p.id}`} />
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-auto">
              {p.red_alert_count > 0 && <span className="badge-pill bg-red-50 text-red-800 border-red-200"><AlertTriangle className="w-3.5 h-3.5" /> {p.red_alert_count} red</span>}
              {!p.scoped && p.completeness != null && <Completeness value={p.completeness} testId={`completeness-${p.id}`} />}
              {p.consent && <ConsentBadge active={p.consent.insurance_access} label="Insurance" testId={`consent-ins-${p.id}`} />}
            </div>
          </Link>
        ))}
        {list.length === 0 && <div className="clinical-card p-10 text-center text-sm text-slate-500 md:col-span-2" data-testid="patients-empty">No patients visible for your role.</div>}
      </div>
    </div>
  );
}
